import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import { GetParam, CreateParam, UpdateParam } from "../ParamIO/api"
import { GetCMSContent } from "aldoo-ra/CMS/api"
import { showNotification } from "aldoo-ra/Notification"
import { ComputerDesktopIcon } from "@heroicons/react/24/outline"

const BrowserThemeAutoDetection = ({ onSettingsChange }) => {
  const [autoDetectionEnabled, setAutoDetectionEnabled] = useState(false)
  const [lightModeTheme, setLightModeTheme] = useState(null)
  const [darkModeTheme, setDarkModeTheme] = useState(null)
  const [availableThemes, setAvailableThemes] = useState([])
  const [loading, setLoading] = useState(true)
  const [isClient, setIsClient] = useState(false)
  
  // Parameter addresses for configuration
  const PARAMS = {
    AUTO_DETECTION: 'SITE_THEME_AUTO_ADAPT',
    LIGHT_THEME: 'SITE_THEME_AUTO_ADAPT_LIGHT',
    DARK_THEME: 'SITE_THEME_AUTO_ADAPT_DARK'
  }

  useEffect(() => {
    // Ensure this only runs on the client
    setIsClient(true)
  }, [])

  useEffect(() => {
    if (isClient) {
      loadConfiguration()
    }
  }, [isClient])

  const loadConfiguration = async () => {
    try {
      setLoading(true)
      
      // Load available themes
      const themesResponse = await GetCMSContent({
        match: {
          "content.type": "SiteTheme",
          deleted: { $ne: true }
        }
      })
      
      let formattedThemes = []
      if (themesResponse.data) {
        // Format themes for dropdown using themeID instead of _id
        formattedThemes = themesResponse.data.map(theme => ({
          value: theme.content.themeID || theme._id, // Fallback to _id if themeID doesn't exist yet
          name: theme.content.name,
          description: theme.content.description
        }))
        setAvailableThemes(formattedThemes)
      }

      // Load current configuration from ParamIO
      const [autoDetection, lightTheme, darkTheme] = await Promise.all([
        GetParam(PARAMS.AUTO_DETECTION).catch(() => null),
        GetParam(PARAMS.LIGHT_THEME).catch(() => null),
        GetParam(PARAMS.DARK_THEME).catch(() => null)
      ])
      
      setAutoDetectionEnabled(autoDetection?.value === true || autoDetection?.value === 'true')
      
      if (lightTheme?.value) {
        const lightThemeData = formattedThemes.find(t => t.value === lightTheme.value)
        setLightModeTheme(lightThemeData ? [lightThemeData] : [])
      }
      
      if (darkTheme?.value) {
        const darkThemeData = formattedThemes.find(t => t.value === darkTheme.value)
        setDarkModeTheme(darkThemeData ? [darkThemeData] : [])
      }
      
    } catch (error) {
      console.error('Error loading browser theme configuration:', error)
      showNotification({
        message: 'Failed to load browser theme settings - ParamIO service may not be running',
        className: "bg-admin_error text-admin_text_inv",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleAutoDetectionToggle = async (enabled) => {
    try {
      const paramData = {
        address: PARAMS.AUTO_DETECTION,
        value: enabled,
        description: 'Enable automatic browser theme adaptation'
      }
      
      // Try to get existing param first
      const existing = await GetParam(PARAMS.AUTO_DETECTION).catch(() => null)
      
      if (existing) {
        await UpdateParam(paramData)
      } else {
        await CreateParam(paramData)
      }
      
      setAutoDetectionEnabled(enabled)
      
      // Notify parent component of settings change
      if (onSettingsChange && typeof onSettingsChange === 'function') {
        onSettingsChange()
      }
      
      showNotification({
        message: `Browser theme auto-detection ${enabled ? 'enabled' : 'disabled'}`,
        className: "bg-admin_success text-admin_text_inv",
      })
      
    } catch (error) {
      console.error('Error updating auto-detection setting:', error)
      showNotification({
        message: 'Failed to update auto-detection setting',
        className: "bg-admin_error text-admin_text_inv",
      })
    }
  }


  const handleThemeSelection = async (themeType, selectedThemes) => {
    try {
      const themeId = selectedThemes.length > 0 ? selectedThemes[0].value : null
      const paramAddress = themeType === 'light' ? PARAMS.LIGHT_THEME : PARAMS.DARK_THEME
      
      const paramData = {
        address: paramAddress,
        value: themeId,
        description: `Theme ID for ${themeType} mode auto-adaptation`
      }
      
      // Try to get existing param first
      const existing = await GetParam(paramAddress).catch(() => null)
      
      if (themeId) {
        if (existing) {
          await UpdateParam(paramData)
        } else {
          await CreateParam(paramData)
        }
      } else if (existing) {
        // If no theme selected and param exists, we could delete it or set to null
        await UpdateParam({ ...paramData, value: null })
      }
      
      if (themeType === 'light') {
        setLightModeTheme(selectedThemes)
      } else {
        setDarkModeTheme(selectedThemes)
      }
      
      showNotification({
        message: `${themeType === 'light' ? 'Light' : 'Dark'} mode theme ${themeId ? 'updated' : 'cleared'}`,
        className: "bg-admin_success text-admin_text_inv",
      })
      
    } catch (error) {
      console.error('Error updating theme selection:', error)
      showNotification({
        message: 'Failed to update theme selection',
        className: "bg-admin_error text-admin_text_inv",
      })
    }
  }

  // Don't render anything on server - completely client-only
  if (!isClient) {
    return null
  }

  if (loading) {
    return (
      <div className="bg-admin_canvas rounded-lg p-6 mb-6">
        <div className="animate-pulse">
          <div className="h-6 bg-admin_neutral_2 rounded w-64 mb-4"></div>
          <div className="h-4 bg-admin_neutral_2 rounded w-full mb-2"></div>
          <div className="h-4 bg-admin_neutral_2 rounded w-3/4"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-paper rounded-lg border border-admin_bordered p-6 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <ComputerDesktopIcon className="w-6 h-6 text-admin_primary" />
        <Typography 
          component="h1"
          variant="admin_h4" 
          className="text-admin_text"
        >
          Browser Theme Auto-Detection
        </Typography>
      </div>
      
      <Typography 
        variant="admin_body_dim" 
        className="mb-6 mt-0"
      >
        Automatically switch themes based on user's browser/system preference (prefers-color-scheme). 
        When enabled, the website will detect if the user has light or dark mode set in their system and apply the corresponding theme.
      </Typography>

      {/* Auto-Detection Toggle */}
      <div className="flex items-center justify-between p-4 bg-admin_paper rounded-lg mb-4 border border-admin_bordered">
        <div>
          <Typography variant="admin_label">
            Enable Auto-Detection
          </Typography>

          <Typography variant="admin_txt1_dim">
            Automatically detect and apply themes based on browser preference
          </Typography>
        </div>

        <ToggleSwitch
          variant="admin"
          isOn={autoDetectionEnabled}
          onChange={handleAutoDetectionToggle}
          size="md"
        />
      </div>

      {/* Theme Selection (only show when auto-detection is enabled) */}
      {autoDetectionEnabled && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Light Mode Theme */}
          <div className="p-4 bg-admin_paper rounded-lg border border-admin_bordered">
            <Typography variant="admin_label">
              Light Mode Theme
            </Typography>

            <Typography 
              variant="admin_txt1_dim" 
              className="block mb-3"
            >
              Theme to apply when user has light mode preference
            </Typography>

            <DropdownSelect
              variant="admin"
              value={lightModeTheme || []}
              onChange={(selected) => handleThemeSelection('light', selected)}
              options={availableThemes}
              placeholder="Select theme for light mode..."
              multiSelection={false}
              showCheckbox={false}
              showSelectionTick={true}
              enableResetButton={true}
              searchEnabled={true}
              menuPosition="top"
              className="w-full"
            />
          </div>

          {/* Dark Mode Theme */}
          <div className="bg-admin_paper rounded-lg border border-admin_bordered p-4">
            <Typography variant="admin_label">
              Dark Mode Theme
            </Typography>

            <Typography 
              variant="admin_txt1_dim" 
              className="block mb-3"
            >
              Theme to apply when user has dark mode preference
            </Typography>

            <DropdownSelect
              variant="admin"
              value={darkModeTheme || []}
              onChange={(selected) => handleThemeSelection('dark', selected)}
              options={availableThemes}
              placeholder="Select theme for dark mode..."
              multiSelection={false}
              showCheckbox={false}
              showSelectionTick={true}
              enableResetButton={true}
              searchEnabled={true}
              menuPosition="top"
              className="w-full"
            />
          </div>
        </div>
      )}

    </div>
  )
}

export default BrowserThemeAutoDetection